import axiosInstance from "./axios";
import i18n from "@/i18n";
import { getAnchorPlanAxiosPrefix } from "@/utils/anchor-plan/helpers";

/** Узгоджено з APP_CONFIG.api.baseURL (той самий origin/проксі, що й основний axios). */
const PREFIX = getAnchorPlanAxiosPrefix();

export interface AnchorPlanUser {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
  is_admin: boolean;
}

export interface AnchorPlanMeResponse {
  user: AnchorPlanUser | null;
}

/** Результат GET https://api4.seranking.com/sites при перевірці глобального ключа (адмін). */
export interface SerankingAdminProbe {
  ok: boolean;
  skipped?: boolean;
  http_status: number | null;
  message: string;
  endpoint: string;
  sites_count?: number;
  /** Сира відповідь SE Ranking (pretty JSON за можливості), може бути скорочена на бекенді */
  response_body: string | null;
  response_truncated?: boolean;
  /** Як саме зроблено запит: header+query або резерв лише query (діагностика) */
  probe_transport?: string;
}

export interface AnchorPlanProjectListItem {
  id: string;
  name: string;
  brand_name?: string | null;
  domain?: string | null;
  created_at?: string;
  updated_at?: string;
  pages_count?: number;
  plan_count?: number;
  owner_id?: number | null;
  owner_name?: string | null;
  owner_email?: string | null;
  my_role?: "owner" | "editor" | "viewer" | null;
  can_edit?: boolean;
  can_share?: boolean;
  /** Частина ключів налаштувань (бейджі оновлень тощо) */
  settings?: Record<string, unknown>;
}

export interface AnchorPlanNotificationRow {
  id: string;
  type: string;
  data: {
    title?: string;
    body?: string;
    /** seranking_positions | seranking_positions_auto | google_sheets_positions_auto | anchor_plan_daily_digest | collaborator_purchase_placements | collaborator_project_links */
    notif_type?: string;
    /** Короткий підпис джерела для дзвінка, наприклад «SE Ranking API» */
    source_label?: string;
    project_id?: string;
    project_name?: string;
    plan_id?: string;
    plan_name?: string;
    link_path?: string;
  };
  read_at: string | null;
  created_at: string | null;
}

export interface AnchorPlanNotificationsResponse {
  data: AnchorPlanNotificationRow[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  unread_count: number;
}

export interface AnchorPlanProjectsResponse {
  projects: AnchorPlanProjectListItem[];
  is_admin?: boolean;
  user?: AnchorPlanUser | null;
}

export interface AnchorPlanServiceAdminRow {
  id: number;
  email: string;
  created_at: string | null;
  created_by: number | null;
}

export interface AnchorPlanAdminsListResponse {
  admins: AnchorPlanServiceAdminRow[];
}

export interface AnchorPlanAdminAddResponse {
  ok: boolean;
  duplicate?: boolean;
  admin?: AnchorPlanServiceAdminRow;
}

export interface AnchorPlanProjectMember {
  user_id: number;
  email: string | null;
  name: string | null;
  role: "owner" | "editor" | "viewer";
  created_at: string | null;
}

export const anchorPlanService = {
  me(): Promise<{ data: AnchorPlanMeResponse }> {
    return axiosInstance.get(`${PREFIX}/auth/me`);
  },

  logout(): Promise<unknown> {
    return axiosInstance.post(`${PREFIX}/auth/logout`);
  },

  listNotifications(params?: {
    per_page?: number;
  }): Promise<{ data: AnchorPlanNotificationsResponse }> {
    const q = new URLSearchParams();
    if (params?.per_page) q.set("per_page", String(params.per_page));
    const qs = q.toString();
    return axiosInstance.get(`${PREFIX}/notifications${qs ? "?" + qs : ""}`);
  },

  markNotificationRead(
    id: string,
  ): Promise<{ data: { ok: boolean; unread_count: number } }> {
    return axiosInstance.post(
      `${PREFIX}/notifications/${encodeURIComponent(id)}/read`,
    );
  },

  markAllNotificationsRead(): Promise<{
    data: { ok: boolean; unread_count: number };
  }> {
    return axiosInstance.post(`${PREFIX}/notifications/read-all`);
  },

  listProjects(): Promise<{ data: AnchorPlanProjectsResponse }> {
    return axiosInstance.get(`${PREFIX}/projects`);
  },

  getProject(projectId: string): Promise<{ data: Record<string, unknown> }> {
    return axiosInstance.get(`${PREFIX}/projects/${projectId}`);
  },

  /** Реєстр сторінок (analysis), окремо від GET /projects/:id для меншої відповіді. */
  getProjectRegistry(
    projectId: string,
  ): Promise<{ data: { analysis: unknown[] } }> {
    return axiosInstance.get(`${PREFIX}/projects/${projectId}/registry`);
  },

  createProject(body: {
    name: string;
    domain?: string;
    brand_name?: string;
  }): Promise<{ data: Record<string, unknown> }> {
    return axiosInstance.post(`${PREFIX}/projects`, body);
  },

  deleteProject(projectId: string): Promise<unknown> {
    return axiosInstance.delete(`${PREFIX}/projects/${projectId}`);
  },

  deletePositionSource(
    projectId: string,
    source: "csv_file" | "google_sheets" | "seranking",
  ): Promise<{ data: Record<string, unknown> }> {
    return axiosInstance.delete(
      `${PREFIX}/projects/${projectId}/sources/positions/${source}`,
    );
  },

  deleteBacklinkSource(
    projectId: string,
    source: "collaborator_api" | "ahrefs_file" | "collaborator_file",
  ): Promise<{ data: Record<string, unknown> }> {
    return axiosInstance.delete(
      `${PREFIX}/projects/${projectId}/sources/backlinks/${source}`,
    );
  },

  /** Адміністратори сервісу Anchor Plan (лише для is_admin) */
  listAnchorAdmins(): Promise<{ data: AnchorPlanAdminsListResponse }> {
    return axiosInstance.get(`${PREFIX}/admin/anchor-admins`);
  },

  addAnchorAdmin(email: string): Promise<{ data: AnchorPlanAdminAddResponse }> {
    return axiosInstance.post(`${PREFIX}/admin/anchor-admins`, { email });
  },

  removeAnchorAdmin(id: number): Promise<{ data: { ok: boolean } }> {
    return axiosInstance.delete(`${PREFIX}/admin/anchor-admins/${id}`);
  },

  listProjectMembers(
    projectId: string,
  ): Promise<{ data: { members: AnchorPlanProjectMember[] } }> {
    return axiosInstance.get(`${PREFIX}/projects/${projectId}/members`);
  },

  addProjectMember(
    projectId: string,
    payload: { email: string; role: "viewer" | "editor" },
  ): Promise<{ data: { ok: boolean; member?: AnchorPlanProjectMember } }> {
    return axiosInstance.post(
      `${PREFIX}/projects/${projectId}/members`,
      payload,
    );
  },

  updateProjectMemberRole(
    projectId: string,
    userId: number,
    payload: { role: "viewer" | "editor" },
  ): Promise<{ data: { ok: boolean } }> {
    return axiosInstance.patch(
      `${PREFIX}/projects/${projectId}/members/${userId}`,
      payload,
    );
  },

  removeProjectMember(
    projectId: string,
    userId: number,
  ): Promise<{ data: { ok: boolean } }> {
    return axiosInstance.delete(
      `${PREFIX}/projects/${projectId}/members/${userId}`,
    );
  },

  /** Лише для is_admin: зміна власника / видимості проєкту. */
  adminPatchProject(
    projectId: string,
    body: { owner_id?: number; visibility?: "private" | "shared" | "public" },
  ): Promise<{
    data: {
      ok: boolean;
      visibility?: string;
      owner_id?: number | null;
      owner_email?: string | null;
      owner_name?: string | null;
    };
  }> {
    return axiosInstance.patch(
      `${PREFIX}/admin/projects/${encodeURIComponent(projectId)}`,
      body,
    );
  },

  adminStats(
    dateFrom?: string,
    dateTo?: string,
    teamId?: string | null,
    options?: { includeLists?: boolean },
  ): Promise<{ data: Record<string, any> }> {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (teamId) params.set("team_id", teamId);
    if (options?.includeLists === false) params.set("include_lists", "0");
    const qs = params.toString();
    return axiosInstance.get(`${PREFIX}/admin/stats${qs ? "?" + qs : ""}`);
  },

  adminStatsTimeseries(
    dateFrom: string,
    dateTo: string,
    teamId?: string | null,
  ): Promise<{
    data: {
      meta: Record<string, unknown>;
      series: Array<{
        date: string;
        anchors_exported: number;
        plan_snapshots: number;
        users_registered: number;
        api_seranking: number;
        api_collaborator: number;
      }>;
    };
  }> {
    const params = new URLSearchParams();
    params.set("date_from", dateFrom);
    params.set("date_to", dateTo);
    if (teamId) params.set("team_id", teamId);
    return axiosInstance.get(
      `${PREFIX}/admin/stats/timeseries?${params.toString()}`,
    );
  },

  adminUserTeamsStatsOverview(
    dateFrom?: string,
    dateTo?: string,
  ): Promise<{
    data: {
      meta: Record<string, unknown>;
      teams: Array<{
        team_id: string;
        name: string;
        members_count: number;
        anchors_exported: number;
        snapshots_count: number;
        api_requests: number;
      }>;
    };
  }> {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    const qs = params.toString();
    return axiosInstance.get(
      `${PREFIX}/admin/user-teams/stats-overview${qs ? "?" + qs : ""}`,
    );
  },

  adminExternalApiUsage(
    dateFrom?: string,
    dateTo?: string,
    teamId?: string | null,
  ): Promise<{ data: Record<string, any> }> {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (teamId) params.set("team_id", teamId);
    const qs = params.toString();
    return axiosInstance.get(
      `${PREFIX}/admin/external-api-usage${qs ? "?" + qs : ""}`,
    );
  },

  /** Серверний експорт CSV / ZIP (той самий період і команда, що й у статистиці). */
  async adminStatisticsExportBlob(params: {
    kind: string;
    date_from?: string;
    date_to?: string;
    team_id?: string;
  }): Promise<Blob> {
    const q = new URLSearchParams();
    q.set("kind", params.kind);
    if (params.date_from) q.set("date_from", params.date_from);
    if (params.date_to) q.set("date_to", params.date_to);
    if (params.team_id) q.set("team_id", params.team_id);
    const res = await axiosInstance.get(
      `${PREFIX}/admin/statistics/export?${q.toString()}`,
      {
        responseType: "blob",
      },
    );
    const ct = String(res.headers["content-type"] || "");
    if (ct.includes("application/json")) {
      const text = await (res.data as Blob).text();
      let msg = i18n.global.t("anchorPlan.components.service.exportError");
      try {
        const j = JSON.parse(text) as { error?: string };
        if (j.error) msg = j.error;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    return res.data as Blob;
  },

  adminUserTeamsList(): Promise<{
    data: {
      teams: Array<{
        id: string;
        name: string;
        created_at: string;
        updated_at: string;
        members_count: number;
      }>;
    };
  }> {
    return axiosInstance.get(`${PREFIX}/admin/user-teams`);
  },

  adminUserTeamCreate(
    name: string,
  ): Promise<{ data: { id: string; name: string } }> {
    return axiosInstance.post(`${PREFIX}/admin/user-teams`, { name });
  },

  adminUserTeamUpdate(
    teamId: string,
    name: string,
  ): Promise<{ data: { ok: boolean } }> {
    return axiosInstance.patch(
      `${PREFIX}/admin/user-teams/${encodeURIComponent(teamId)}`,
      { name },
    );
  },

  adminUserTeamDelete(teamId: string): Promise<{ data: { ok: boolean } }> {
    return axiosInstance.delete(
      `${PREFIX}/admin/user-teams/${encodeURIComponent(teamId)}`,
    );
  },

  adminUserTeamShow(teamId: string): Promise<{
    data: {
      team: {
        id: string;
        name: string;
        created_at: string;
        updated_at: string;
      };
      members: Array<{ user_id: number; email: string; name: string | null }>;
    };
  }> {
    return axiosInstance.get(
      `${PREFIX}/admin/user-teams/${encodeURIComponent(teamId)}`,
    );
  },

  adminUserTeamAddMember(
    teamId: string,
    userId: number,
  ): Promise<{ data: { ok: boolean } }> {
    return axiosInstance.post(
      `${PREFIX}/admin/user-teams/${encodeURIComponent(teamId)}/members`,
      {
        user_id: userId,
      },
    );
  },

  adminUserTeamRemoveMember(
    teamId: string,
    userId: number,
  ): Promise<{ data: { ok: boolean } }> {
    return axiosInstance.delete(
      `${PREFIX}/admin/user-teams/${encodeURIComponent(teamId)}/members/${userId}`,
    );
  },

  /** Коефіцієнти хвилин лінкбілдингу (адмін). Значення — оцінка. */
  adminSettings(): Promise<{
    data: {
      minutes_per_url: number;
      minutes_per_anchor_build: number;
      minutes_per_donor_match: number;
    };
  }> {
    return axiosInstance.get(`${PREFIX}/admin/settings`);
  },

  patchAdminSettings(body: {
    minutes_per_url?: number;
    minutes_per_anchor_build?: number;
    minutes_per_donor_match?: number;
  }): Promise<{ data: Record<string, any> }> {
    return axiosInstance.patch(`${PREFIX}/admin/settings`, body);
  },

  suggestUsersForAdmins(q: string): Promise<{
    data: { users: Array<{ id: number; email: string; name: string | null }> };
  }> {
    return axiosInstance.get(
      `${PREFIX}/admin/users/suggest?q=${encodeURIComponent(q)}&limit=15&exclude_admins=1`,
    );
  },

  maintenance(): Promise<{ data: Record<string, any> }> {
    return axiosInstance.get(`${PREFIX}/admin/maintenance`);
  },

  /** Live-зріз навантаження по процесах (host + engine + черги + активні + throughput + external). */
  adminLoadLive(params?: {
    throughput_window_minutes?: number;
    external_window_minutes?: number;
  }): Promise<{ data: Record<string, any> }> {
    return axiosInstance.get(`${PREFIX}/admin/load/live`, { params });
  },

  /** Історичні time-series навантаження (Фаза 2, потребує таблиці семплів). */
  adminLoadHistory(params?: {
    from?: string;
    to?: string;
    bucket_seconds?: number;
  }): Promise<{ data: Record<string, any> }> {
    return axiosInstance.get(`${PREFIX}/admin/load/history`, { params });
  },

  deleteAnchorPlanQueueJob(payload: {
    uuid: string;
    queue: string;
  }): Promise<{ data: { ok: boolean; action?: string; error?: string } }> {
    return axiosInstance.delete(`${PREFIX}/admin/maintenance/queue-jobs`, {
      data: payload,
    });
  },

  deleteAnchorPlanQueueJobHistoryRow(
    id: number,
  ): Promise<{ data: { ok: boolean; error?: string } }> {
    return axiosInstance.delete(
      `${PREFIX}/admin/maintenance/queue-job-history/${id}`,
    );
  },

  /** PHPUnit групи anchor-plan-production (за ANCHOR_PLAN_ALLOW_WEB_TEST_RUNNER та наявності vendor/phpunit). */
  runAnchorPlanProductionTests(): Promise<{ data: Record<string, unknown> }> {
    return axiosInstance.post(`${PREFIX}/admin/run-production-tests`, {});
  },

  adminLogs(
    params: Record<string, any> = {},
  ): Promise<{ data: Record<string, any> }> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const q = qs.toString();
    return axiosInstance.get(`${PREFIX}/admin/logs${q ? "?" + q : ""}`);
  },

  deleteLog(id: number): Promise<{ data: { success: boolean } }> {
    return axiosInstance.delete(`${PREFIX}/admin/logs/${id}`);
  },

  clearLogs(
    olderThanDays?: number,
  ): Promise<{ data: { success: boolean; deleted: number } }> {
    const params: Record<string, any> = {};
    if (olderThanDays) params.older_than_days = olderThanDays;
    return axiosInstance.delete(`${PREFIX}/admin/logs`, { params });
  },

  clearCache(): Promise<{ data: { success: boolean } }> {
    return axiosInstance.post(`${PREFIX}/admin/maintenance/clear-cache`);
  },

  clearCompetitorsCache(): Promise<{
    data: { success: boolean; deleted: number };
  }> {
    return axiosInstance.post(
      `${PREFIX}/admin/maintenance/clear-competitors-cache`,
    );
  },

  getDefaultDonors(): Promise<{
    data: { count: number; uploaded_at: string | null };
  }> {
    return axiosInstance.get(`${PREFIX}/admin/default-donors`);
  },

  uploadDefaultDonors(
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<{ data: { count: number; uploaded_at: string } }> {
    const fd = new FormData();
    fd.append("file", file);
    return axiosInstance.post(`${PREFIX}/admin/default-donors/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e: any) => {
        if (onProgress && e.total)
          onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },

  deleteDefaultDonors(): Promise<{ data: { ok: boolean } }> {
    return axiosInstance.delete(`${PREFIX}/admin/default-donors`);
  },

  getSerankingAdminSettings(): Promise<{
    data: {
      seranking_api_key_set: boolean;
      seranking_probe?: SerankingAdminProbe;
    };
  }> {
    return axiosInstance.get(`${PREFIX}/admin/seranking-settings`);
  },

  patchSerankingAdminSettings(body: {
    seranking_api_key?: string;
    clear_seranking_api_key?: boolean;
  }): Promise<{
    data: {
      seranking_api_key_set: boolean;
      seranking_probe?: SerankingAdminProbe;
      error?: string;
    };
  }> {
    return axiosInstance.patch(`${PREFIX}/admin/seranking-settings`, body);
  },

  /**
   * Генерація Collaborator CSV (імпорт публікацій) з текстів у Google Docs.
   * Великі списки обробляйте кількома викликами: лише перший із `include_csv_header: true`,
   * статистику — лише на останньому з `log_export_event: true` та `prior_documents_count`.
   */
  async generateCollaboratorPublicationsCsv(body: {
    urls: string[];
    include_csv_header?: boolean;
    log_export_event?: boolean;
    prior_documents_count?: number;
  }): Promise<{ blob: Blob; documentsInChunk: number }> {
    const res = await axiosInstance.post<Blob>(
      `${PREFIX}/collaborator-csv/generate`,
      body,
      {
        responseType: "blob",
        validateStatus: () => true,
      },
    );

    const ctype = String(res.headers["content-type"] ?? "").toLowerCase();
    const raw = res.data;

    const readBlobText = async (blob: Blob | undefined): Promise<string> => {
      if (!blob || typeof blob.text !== "function") return "";
      try {
        return await blob.text();
      } catch {
        return "";
      }
    };

    const extractErrorMessage = (
      text: string,
      fallback: string,
    ): string => {
      const t = text.trim();
      if (!t) return fallback;
      try {
        const parsed = JSON.parse(t) as { error?: string };
        return parsed.error || t;
      } catch {
        return t;
      }
    };

    if (res.status >= 400) {
      const text = await readBlobText(raw);
      throw new Error(
        extractErrorMessage(
          text,
          i18n.global.t("anchorPlan.components.service.httpError", {
            status: res.status,
          }),
        ),
      );
    }

    if (ctype.includes("application/json")) {
      const text = await readBlobText(raw);
      throw new Error(
        extractErrorMessage(
          text,
          i18n.global.t("anchorPlan.components.service.unexpectedJson"),
        ),
      );
    }

    if (!(raw instanceof Blob)) {
      throw new Error(i18n.global.t("anchorPlan.components.service.emptyResponse"));
    }

    const hdr = res.headers as Record<string, string | undefined>;
    const docHdr =
      hdr["x-collaborator-csv-documents"] ?? hdr["X-Collaborator-Csv-Documents"];
    const documentsInChunk = Math.max(
      0,
      parseInt(String(docHdr ?? "").trim(), 10) || 0,
    );

    return { blob: raw, documentsInChunk };
  },
};
